import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import { useSectionHeadSlot } from '../cms/SectionShell';
import { staleReason, undecidedOutsideWeek } from '../cms/missedPost';
import { todayWindow, useReachDecisions } from '../cms/todayData';
import { PostPanel, TONE } from './PostPanel';
import { usePostActions } from './postActions';
import { useFreshPost } from './FreshPost';
import { WritePost } from './WritePost';
import { AgentFirstButton } from '../cms/AskYourAgent';
import { PostWork } from './PostWork';
import { useChannelReconnect } from './ChannelPicker';
import { compact, PostVisits, REACH_POST_VISITS } from './reachVisits';
import { unsharedLine } from './unsharedLine';
import { useVoiceRewrite } from './useVoiceRewrite';
import {
  addDays,
  campaignDecision,
  channelLabel,
  channelMark,
  channelName,
  connectionExpired,
  dayTime,
  DRAFT_POSTS_FOR_NEW_ARTICLES,
  andList,
  postText,
  postTime,
  REACH_CALENDAR,
  REACH_CAMPAIGN_NAMES,
  REACH_CHANNEL_ROWS,
  REACH_CHANNELS,
  REACH_UNSHARED,
  Channel,
  ReachPost,
  time,
  toDate,
} from './reach';

/**
 * Reach → Calendar ("tinycms UX optimization review v6", Prototype v6): what
 * is waiting on the person first (approve or skip each post), then the week —
 * posted, scheduled and waiting, colour-coded. An agent's post on a channel set
 * to "Ask me first" waits here and can't go out until approved. "Write a post"
 * lets the person post by hand (WritePost.tsx).
 *
 * Posts that didn't go out and still wait for a decision — the list Today and
 * Channels show — but sit outside the week on screen get a strip above the
 * calendar, so someone who works only here still learns of them (UX review
 * round 11). Review opens that channel's list in Channels.
 *
 * "N things need you · Also on Today" (round 12) lists the campaigns waiting
 * on a yes (Review opens them) and the posts waiting for approval; the week's
 * line counts every Reach decision as Today does (useReachDecisions), so the
 * two screens agree.
 */

const DAY_MS = 86400000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** The week (from today, in sevens) that holds `at`: 0 for this one, -1 for the last. */
export const weekOffsetOf = (at: Date | null, now = new Date()) => {
  if (!at || Number.isNaN(at.getTime())) return 0;
  const days = Math.round((startOfDay(at).getTime() - startOfDay(now).getTime()) / DAY_MS);
  return Math.floor(days / 7);
};

/** Pages the empty week's "Share a page" list shows (a new site has a handful). */
const SHARE_PAGES_SHOWN = 8;

export function ReachCalendar() {
  const { accountId } = useAccount();
  const { rewrite: rewriteInVoice, rewriting } = useVoiceRewrite();
  const navigate = useNavigate();
  // ?post=<id>&at=<time>: opened from an article's Reach card on that post's week.
  const [params] = useSearchParams();
  const [offset, setOffset] = useState(() =>
    weekOffsetOf(params.get('at') ? new Date(String(params.get('at'))) : null)
  );
  const start = useMemo(() => {
    const today = startOfDay(new Date());
    return addDays(today, offset * 7);
  }, [offset]);
  const end = useMemo(() => addDays(start, 7), [start]);

  // The bar goes on the header line; once it wraps under the title it spreads
  // out again (counts left, the week and Write a post right).
  const headSlot = useSectionHeadSlot();
  const [barEl, setBarEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const head = headSlot?.parentElement;
    if (!barEl || !head) return undefined;
    const check = () => {
      const title = head.firstElementChild;
      const wrapped = Boolean(
        title && barEl.getBoundingClientRect().top >= title.getBoundingClientRect().bottom
      );
      if (wrapped) barEl.dataset.wrapped = 'true';
      else delete barEl.dataset.wrapped;
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(head);
    ro.observe(barEl);
    return () => ro.disconnect();
  }, [barEl, headSlot]);

  const { data, loading, refetch } = useQuery<{
    reachCalendar: { posts: ReachPost[]; waiting: ReachPost[]; drafts?: ReachPost[] };
  }>(REACH_CALENDAR, {
    variables: { accountId, from: start.toISOString(), to: end.toISOString() },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: channelData } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId,
  });
  // Drafts say which campaign they're filed under (Prototype: "· Fall price guide").
  const { data: campaignNames } = useQuery<{ projects: Array<{ id: string; name: string }> }>(
    REACH_CAMPAIGN_NAMES,
    { variables: { accountId }, skip: !accountId }
  );
  // The channels' undecided posts, in Today's window (as Channels asks, so the
  // three screens count the same posts).
  const since = todayWindow('week').from;
  const { data: rowsData, refetch: refetchRows } = useQuery<{ socialProfiles: Channel[] }>(
    REACH_CHANNEL_ROWS,
    {
      variables: { accountId, since },
      skip: !accountId,
      fetchPolicy: 'cache-and-network',
    }
  );
  const { reconnect, dialogs: reconnectDialogs } = useChannelReconnect({
    accountId,
    onConnected: () => void refetchRows(),
  });
  // Today's Reach decisions: the same count, and the campaigns that wait.
  const decisions = useReachDecisions();
  // "New on your site, not shared yet" (Prototype v14): this week only.
  const { data: unsharedData, refetch: refetchUnshared } = useQuery<{
    reachUnshared: {
      articles: Array<{ id: string; title: string }>;
      pages?: Array<{ id: string; title: string; path: string; link: string }>;
      everPosted?: boolean;
      channels: Array<{ id: string; platform: string }>;
      sharingPaused: boolean;
    };
  }>(REACH_UNSHARED, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [draftNew, { loading: draftingNew }] = useMutation(DRAFT_POSTS_FOR_NEW_ARTICLES);
  const [unsharedAsk, setUnsharedAsk] = useState(false);
  // The post opened in the side panel (any post on the week can be opened).
  const [openId, setOpenId] = useState<string | null>(() => params.get('post'));
  // The card under the pointer shows "+ Note" and ✓ (campaign board work strip).
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  // Share a page: the composer opens with the page's address in Link.
  const [writeLink, setWriteLink] = useState('');
  // Drafts → "+ New draft": the composer starts on Save as draft.
  const [writeDraft, setWriteDraft] = useState(false);
  const write = (link = '', draft = false) => {
    setWriteLink(link);
    setWriteDraft(draft);
    setWriting(true);
  };
  // Drafts → Finish opens the side panel too: anything already on the calendar
  // is viewed and edited there; the modal is only for a new post (2026-10-24).
  const finish = (p: ReachPost) => setOpenId(p.id);

  const posts = useMemo(
    () =>
      (data?.reachCalendar.posts || []).filter(
        (p) => p.reachStatus !== 'skipped' && p.reachStatus !== 'draft'
      ),
    [data]
  );
  const waiting = data?.reachCalendar.waiting || [];
  // Saved with no time: no week holds them, so they are listed on their own.
  const drafts = data?.reachCalendar.drafts || [];
  const openPost = [...posts, ...waiting, ...drafts].find((p) => p.id === openId) || null;
  const connected = (channelData?.socialProfiles || []).filter((c) => c.isActive).length;

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const day = addDays(start, i);
        const next = addDays(day, 1);
        return {
          day,
          posts: posts
            .filter((p) => {
              const t = postTime(p);
              return t && t >= day && t < next;
            })
            .sort((a, b) => (postTime(a)?.getTime() || 0) - (postTime(b)?.getTime() || 0)),
        };
      }),
    [start, posts]
  );

  // Posted cards show the clicks each post sent to the site (and sign-ups
  // that followed), from the site's Google Analytics as last synced.
  const postedIds = useMemo(
    () => days.flatMap((d) => d.posts.filter((p) => p.reachStatus === 'posted').map((p) => p.id)),
    [days]
  );
  const { data: visitData } = useQuery<{ reachPostVisits: PostVisits }>(REACH_POST_VISITS, {
    variables: { accountId, postIds: postedIds },
    skip: !accountId || !postedIds.length,
    fetchPolicy: 'cache-and-network',
  });
  const postVisits = visitData?.reachPostVisits;
  const clicksLive = Boolean(postVisits?.connected && postVisits.syncedAt);
  const visitsOf = new Map((postVisits?.posts || []).map((v) => [v.postId, v]));

  // The panel's buttons (postActions.ts, shared with the campaign board).
  const { busy, act, remove } = usePostActions({
    onDone: () => {
      setOpenId(null);
      // The week, and Today's count of decisions (another window's query).
      return Promise.all([refetch(), decisions.reach.refetch()]);
    },
  });
  // Old news waiting for approval offers a fresh post instead (round 32).
  const fresh = useFreshPost(() => void refetch());

  const unshared = unsharedData?.reachUnshared;
  const newArticles = unshared?.articles || [];
  // Until anything has gone out, live pages count as not shared yet (Prototype v15).
  const newPages = unshared?.pages || [];
  const newCount = newArticles.length + newPages.length;
  // A new site's empty week: nothing on it, nothing ever posted.
  const emptyWeek =
    connected > 0 &&
    Boolean(data && unshared) &&
    unshared?.everPosted === false &&
    !posts.length &&
    !waiting.length;
  const channelWords = andList([
    ...new Set((unshared?.channels || []).map((c) => channelName(c.platform))),
  ]);
  const draftForNew = async () => {
    try {
      const { data: res } = await draftNew({ variables: { accountId } });
      const r = res?.draftPostsForNewArticles;
      setUnsharedAsk(false);
      await Promise.all([refetch(), refetchUnshared()]);
      showInkToast(
        `Drafted ${r?.drafted || 0} ${r?.drafted === 1 ? 'post' : 'posts'}. ${
          r?.drafted === 1 ? 'It’s' : 'They’re'
        } waiting for your approval.${
          r?.skipped ? ` ${r.skipped} skipped: that channel needs a photo or video.` : ''
        }`
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  const undecided = undecidedOutsideWeek(
    (rowsData?.socialProfiles || []).filter((c) => c.isActive),
    start,
    end,
    postTime,
    channelName,
    new Date(),
    connectionExpired
  );

  const range = `${start.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${addDays(
    end,
    -1
  ).toLocaleDateString([], {
    month: start.getMonth() === addDays(end, -1).getMonth() ? undefined : 'short',
    day: 'numeric',
  })}`;
  const todayKey = startOfDay(new Date()).getTime();
  // This week's posts that didn't go out; those from other weeks are `undecided`.
  const missedThisWeek = posts.filter((p) => p.reachStatus === 'missed');
  const needsYou = Boolean(
    decisions.campaigns.length ||
      waiting.length ||
      missedThisWeek.length ||
      undecided ||
      drafts.length
  );

  // The week and Write a post sit on the title and tabs line (UX optimization
  // review, 2026-10-23), on a row of their own when they don't fit there; outside
  // a SectionShell they stay above the week.
  const bar = (
    <div className={headSlot ? 'rc-bar rc-bar-head' : 'rc-bar'} ref={setBarEl}>
      {/* The week and the way to write, together at the right (Prototype v15). */}
      <span className="rc-bar-right">
        <span className="rc-nav">
          <button type="button" aria-label="Previous week" onClick={() => setOffset((o) => o - 1)}>
            ‹
          </button>
          <span className="rc-range">{range}</span>
          <button type="button" aria-label="Next week" onClick={() => setOffset((o) => o + 1)}>
            ›
          </button>
        </span>
        {connected ? (
          <AgentFirstButton action="post" label="Write a post" onSelf={() => write()} />
        ) : null}
      </span>
    </div>
  );

  return (
    <div className="rc">
      {headSlot ? createPortal(bar, headSlot) : headSlot === undefined ? bar : null}

      {connected === 0 && channelData ? (
        <div className="rc-empty">
          <div className="rc-empty-title">No channels yet</div>
          <p>
            Connect X, Threads, Instagram or another channel, and your agent can draft posts for it
            from what you publish.
          </p>
          <Link to="/reach/channels" className="ink-btn ink-btn-primary">
            Connect a channel
          </Link>
        </div>
      ) : null}

      {offset === 0 && newCount && unshared?.channels.length ? (
        <section className="rc-unshared" aria-label="New on your site, not shared yet">
          <span className="ink-dot" style={{ background: 'var(--ink-accent)' }} />
          <div className="rc-unshared-text">
            <strong>New on your site, not shared yet ({newCount})</strong>
            <span className="ink-muted">
              {unsharedAsk
                ? `tinycms drafts a post for each one on ${channelWords}, from its title and summary, and sends them here for approval. Nothing posts until you approve it.`
                : unsharedLine(newArticles, newPages, {
                    paused: Boolean(unshared?.sharingPaused),
                    // The empty week's "Share a page" list shows them all just below.
                    pagesListedBelow: emptyWeek && newPages.length <= SHARE_PAGES_SHOWN,
                  })}
            </span>
          </div>
          {unsharedAsk ? (
            <span className="rc-unshared-actions">
              <button
                type="button"
                className="ink-btn"
                disabled={draftingNew}
                onClick={() => setUnsharedAsk(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={draftingNew}
                onClick={() => void draftForNew()}
              >
                {draftingNew ? 'Drafting…' : `Draft posts for ${newCount}`}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => setUnsharedAsk(true)}
            >
              Draft posts
            </button>
          )}
        </section>
      ) : null}

      {/* What needs you, once, each with its action (backlog 672ace6e). */}
      {needsYou ? (
        <section className="rc-need" aria-label="Needs you">
          <div className="rc-need-head">
            <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
            <strong>Needs you</strong>
          </div>
          {decisions.campaigns.map((c) => {
            const row = campaignDecision(c);
            const review = () => navigate(`/campaigns?campaign=${encodeURIComponent(c.id)}`);
            return (
              <div className="rc-need-row" key={`campaign:${c.id}`}>
                <span className="rc-camp-badge">Campaign</span>
                <button type="button" className="rc-need-text rc-draft-open" onClick={review}>
                  <span className="rc-need-title">{row.title}</span>
                  <span className="rc-need-meta">{row.meta}</span>
                </button>
                <button type="button" className="ink-btn ink-btn-primary" onClick={review}>
                  Review
                </button>
              </div>
            );
          })}
          {waiting.map((p) => {
            const t = postTime(p);
            // More than 48h late, or it names a day that has passed: Skip leads
            // and a fresh post sits beside it, as in Approvals; Approve stays in
            // the post panel (round 32, backlog ae933945).
            const stale = staleReason(postText(p), t);
            return (
              <div className="rc-need-row" key={p.id}>
                <span className="rc-mark" title={channelName(p.platform)}>
                  {channelMark(p.platform)}
                </span>
                <div className="rc-need-text">
                  <div className="rc-need-title">{postText(p) || 'Untitled post'}</div>
                  <div className="rc-need-meta">
                    {channelName(p.platform)}
                    {stale
                      ? ` · ${stale}`
                      : t
                        ? ` · ${dayTime(t)}${t.getTime() < Date.now() ? ' (passed — goes out when approved)' : ''}`
                        : ''}
                    {p.itemCheck?.holds ? ' · Held' : p.voiceCheck?.offVoice ? ' · Off voice' : ''}
                  </div>
                </div>
                {stale ? (
                  <>
                    <button
                      type="button"
                      className="ink-btn"
                      disabled={busy === p.id}
                      onClick={() => void fresh.open(p)}
                    >
                      Write a fresh post
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={busy === p.id}
                      onClick={() => void act(p, 'skip')}
                    >
                      Skip
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="ink-btn ink-btn-quiet"
                      disabled={busy === p.id}
                      onClick={() => void act(p, 'skip')}
                    >
                      Skip
                    </button>
                    {p.itemCheck?.holds ? (
                      <button
                        type="button"
                        className="ink-btn ink-btn-primary"
                        onClick={() => setOpenId(p.id)}
                      >
                        Review
                      </button>
                    ) : p.voiceCheck?.holds ? (
                      <button
                        type="button"
                        className="ink-btn ink-btn-primary"
                        disabled={busy === p.id || rewriting}
                        onClick={() => void rewriteInVoice([{ kind: 'post', id: p.id }])}
                      >
                        Rewrite in your voice
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="ink-btn ink-btn-primary"
                        disabled={busy === p.id}
                        onClick={() => void act(p, 'approve')}
                      >
                        Approve
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
          {missedThisWeek.map((p) => {
            const t = postTime(p);
            return (
              <div className="rc-need-row" key={`missed:${p.id}`}>
                <span className="rc-mark" title={channelName(p.platform)}>
                  {channelMark(p.platform)}
                </span>
                <button
                  type="button"
                  className="rc-need-text rc-draft-open"
                  onClick={() => setOpenId(p.id)}
                >
                  <span className="rc-need-title">{postText(p) || 'Untitled post'}</span>
                  <span className="rc-need-meta">
                    {channelName(p.platform)} · Didn’t post{t ? ` · ${dayTime(t)}` : ''}
                  </span>
                </button>
                <button type="button" className="ink-btn" onClick={() => setOpenId(p.id)}>
                  Review
                </button>
              </div>
            );
          })}
          {undecided ? (
            <div className="rc-need-row" key="undecided">
              <span className="ink-dot rc-need-dot" style={{ background: 'var(--ink-red)' }} />
              <div className="rc-need-text">
                <div className="rc-need-title">{undecided.title}</div>
                <div className="rc-need-meta">{undecided.sub}</div>
              </div>
              {undecided.reconnect ? (
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  onClick={() => reconnect(undecided.reconnect as Channel)}
                >
                  Reconnect {channelName(undecided.reconnect.platform)}
                </button>
              ) : null}
              <button
                type="button"
                className="ink-btn"
                onClick={() =>
                  navigate(`/reach/channels?review=${encodeURIComponent(undecided.channelId)}`)
                }
              >
                Review
              </button>
            </div>
          ) : null}
          {drafts.map((p) => {
            // A campaign's own draft changes through the campaign: no Delete here.
            const own = p.createdVia !== 'campaign';
            const saved = p.updatedAt ? toDate(p.updatedAt) : null;
            const profiles = channelData?.socialProfiles || [];
            const profile = profiles.find((c) => c.id === p.socialProfileId);
            const campaign = p.projectId
              ? campaignNames?.projects.find((c) => c.id === p.projectId)?.name
              : null;
            // "Saved Oct 9 · LinkedIn (Hai Tran) · Fall price guide" (Prototype).
            const meta = [
              saved
                ? `Saved ${saved.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : '',
              profile ? channelLabel(profile, profiles) : channelName(p.platform),
              campaign || '',
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <div className="rc-need-row rc-draft-row" key={p.id}>
                <span className="rc-mark" title={channelName(p.platform)}>
                  {channelMark(p.platform)}
                </span>
                <button
                  type="button"
                  className="rc-need-text rc-draft-open"
                  onClick={() => finish(p)}
                >
                  <span className="rc-need-title">{postText(p) || 'Untitled post'}</span>
                  <span className="rc-need-meta">{meta}</span>
                </button>
                {own ? (
                  <button
                    type="button"
                    className="ink-btn ink-btn-quiet"
                    disabled={busy === p.id}
                    onClick={() => void act(p, 'discard')}
                  >
                    Delete
                  </button>
                ) : null}
                <button type="button" className="ink-btn" onClick={() => finish(p)}>
                  {own ? 'Finish' : 'Open'}
                </button>
              </div>
            );
          })}
        </section>
      ) : null}

      <div
        className="rc-week"
        aria-busy={loading && !data}
        data-empty={emptyWeek ? 'true' : undefined}
      >
        {emptyWeek ? (
          <div className="rc-empty-week">
            <div
              className="rc-empty-week-card"
              role="region"
              aria-label="Nothing planned this week"
            >
              <div>
                <div className="rc-empty-title">Nothing planned this week</div>
                <p>
                  Nothing has gone out from this site yet. Write a first post, or share a page
                  that’s already live.
                </p>
              </div>
              <AgentFirstButton
                action="post"
                label="Write a post"
                align="center"
                wrapClassName="rc-empty-write"
                onSelf={() => write()}
              />
              {newPages.length ? (
                <div className="rc-share-pages">
                  <div className="rc-share-head">SHARE A PAGE</div>
                  {newPages.slice(0, SHARE_PAGES_SHOWN).map((pg) => (
                    <button
                      type="button"
                      key={pg.id}
                      className="rc-share-row"
                      onClick={() => write(pg.link)}
                    >
                      <span className="rc-share-title">{pg.title}</span>
                      <span className="rc-share-path">{pg.path}</span>
                      <span className="rc-share-go">Share</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {days.map(({ day, posts: list }) => {
          const isToday = day.getTime() === todayKey;
          return (
            <div className="rc-day" key={day.getTime()}>
              <div className="rc-day-head">
                <span>
                  {day.toLocaleDateString([], { weekday: 'short' })} <b>{day.getDate()}</b>
                </span>
                {isToday ? <span className="rc-today">TODAY</span> : null}
              </div>
              {list.map((p) => {
                const tone = TONE[p.reachStatus] || TONE.scheduled;
                const t = postTime(p);
                const campaignName = p.projectId
                  ? campaignNames?.projects.find((c) => c.id === p.projectId)?.name
                  : null;
                return (
                  // A div, not a button: the work strip holds buttons of its own.
                  <div
                    role="button"
                    tabIndex={0}
                    className="rc-post"
                    key={p.id}
                    style={{ borderColor: tone.border }}
                    title={tone.label}
                    onClick={() => setOpenId(p.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target === e.currentTarget) setOpenId(p.id);
                    }}
                    onMouseEnter={() => setHoverId(p.id)}
                    onMouseLeave={() => setHoverId((h) => (h === p.id ? null : h))}
                  >
                    <div className="rc-post-head">
                      <span className="ink-dot" style={{ background: tone.dot }} />
                      <span className="rc-post-time">{t ? time(t) : ''}</span>
                      <span className="rc-mark rc-mark-sm">{channelMark(p.platform)}</span>
                    </div>
                    <div className="rc-post-text">{postText(p)}</div>
                    {campaignName ? <div className="rc-post-camp">{campaignName}</div> : null}
                    {p.voiceCheck?.offVoice ? (
                      <div className="rc-post-voice">
                        <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
                        Off voice
                      </div>
                    ) : null}
                    <PostWork
                      post={p}
                      hovered={hoverId === p.id}
                      onChanged={() => void refetch()}
                    />
                    {p.reachStatus === 'posted' &&
                    (clicksLive ||
                      visitsOf.get(p.id)?.signups ||
                      visitsOf.get(p.id)?.views != null) ? (
                      <PostedStats v={visitsOf.get(p.id)} clicks={clicksLive} />
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="rc-legend">
        {(['posted', 'scheduled', 'waiting', 'missed', 'draft'] as const).map((k) => (
          <span key={k}>
            <span className="ink-dot" style={{ background: TONE[k].dot }} />
            {TONE[k].label}
          </span>
        ))}
      </div>
      {openPost ? (
        <PostPanel
          post={openPost}
          busy={busy === openPost.id}
          onClose={() => setOpenId(null)}
          onAct={(kind, at, reason) => void act(openPost, kind, at, reason)}
          // Edit and Delete happen in the panel (UX optimization review, 2026-10-24).
          onDelete={() => void remove(openPost)}
          onReconnected={() => refetch()}
          onSaved={() => {
            setOpenId(null);
            return Promise.all([refetch(), decisions.reach.refetch()]);
          }}
        />
      ) : null}
      {fresh.node}
      {writing ? (
        <WritePost
          link={writeLink}
          when={writeDraft ? 'draft' : undefined}
          onClose={() => setWriting(false)}
          onPosted={() => {
            setWriting(false);
            void refetch();
          }}
        />
      ) : null}
      {reconnectDialogs}
    </div>
  );
}

/** "1.2K views · 42 clicks · 3 sign-ups" under a posted card. */
function PostedStats({
  v,
  clicks,
}: {
  v?: { visits: number; signups: number; views: number | null };
  clicks: boolean;
}) {
  const n = v?.visits || 0;
  const s = v?.signups || 0;
  const views = v?.views;
  const parts = [
    views != null ? `${compact(views)} ${views === 1 ? 'view' : 'views'}` : null,
    clicks ? `${n.toLocaleString()} ${n === 1 ? 'click' : 'clicks'}` : null,
    s ? `${s.toLocaleString()} ${s === 1 ? 'sign-up' : 'sign-ups'}` : null,
  ].filter(Boolean);
  return (
    <div
      className="rc-post-stats"
      title="Views on the network, visits to your site from this post, and sign-ups after"
    >
      {parts.join(' · ')}
    </div>
  );
}
